import type { FC } from "react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { ACCOUNT_TYPES } from "@/shared/constants/estateCategories";
import { currencyInput } from "@/shared/utils/currencyInput";

const ITEM_TYPES = ACCOUNT_TYPES["Valuables"];

export interface PhysicalAssetFormValues {
  itemType: string;
  description: string;
  value: string;
  location: string;
  file: File | null;
}

export const EMPTY_PHYSICAL_ASSET_FORM: PhysicalAssetFormValues = {
  itemType: "",
  description: "",
  value: "",
  location: "",
  file: null,
};

export interface PhysicalAssetFieldErrors {
  itemType?: string;
  description?: string;
}

export function validatePhysicalAssetFields(
  form: PhysicalAssetFormValues,
): PhysicalAssetFieldErrors {
  const errors: PhysicalAssetFieldErrors = {};
  if (!form.itemType) errors.itemType = "Please select an item type";
  if (!form.description.trim()) errors.description = "Please enter a description";
  return errors;
}

export function physicalAssetToItemFields(form: PhysicalAssetFormValues) {
  return {
    item_type: form.itemType || undefined,
    balance: parseFloat(form.value) || 0,
    notes: form.location.trim() || undefined,
  };
}

export function parsePhysicalAssetToForm(
  itemType: string,
  balance: number,
  notes?: string | null,
): PhysicalAssetFormValues {
  return {
    itemType: itemType || "",
    description: "",
    value: balance ? String(balance) : "",
    location: notes || "",
    file: null,
  };
}

export function buildPhysicalAssetSubtitle(
  itemType?: string | null,
  balance?: number,
  location?: string | null,
): string {
  const parts: string[] = [];
  if (itemType) parts.push(itemType);
  if (balance) {
    parts.push(
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(balance),
    );
  }
  if (location?.trim()) parts.push(location.trim());
  return parts.join(" \u00B7 ");
}

interface PhysicalAssetFormProps {
  form: PhysicalAssetFormValues;
  onChange: (form: PhysicalAssetFormValues) => void;
  fieldErrors?: PhysicalAssetFieldErrors;
  showUpload?: boolean;
  existingFileName?: string | null;
  onFileRemoved?: () => void;
}

export const PhysicalAssetForm: FC<PhysicalAssetFormProps> = ({
  form,
  onChange,
  fieldErrors,
  showUpload = true,
  existingFileName,
  onFileRemoved,
}) => {
  const valueInput = currencyInput(form.value, (val) => onChange({ ...form, value: val }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium" required>
          Item type
        </Label>
        <Select value={form.itemType} onValueChange={(val) => onChange({ ...form, itemType: val })}>
          <SelectTrigger className={`text-sm ${fieldErrors?.itemType ? "border-error" : ""}`}>
            <SelectValue placeholder="Select item type" />
          </SelectTrigger>
          <SelectContent>
            {ITEM_TYPES.map((type) => (
              <SelectItem key={type} value={type} className="text-sm">
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError message={fieldErrors?.itemType} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium" required>
          Description
        </Label>
        <Input
          value={form.description}
          onChange={(e) => onChange({ ...form, description: e.target.value })}
          placeholder="e.g., Diamond engagement ring, Rolex watch, Picasso painting"
          className={fieldErrors?.description ? "border-error" : ""}
        />
        <FieldError message={fieldErrors?.description} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Approximate value <span className="text-tertiary">(optional)</span>
          </Label>
          <CurrencyInput
            value={valueInput.displayValue}
            onChange={valueInput.handleChange}
            placeholder="0.00"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Where is it located? <span className="text-tertiary">(optional)</span>
          </Label>
          <Input
            value={form.location}
            onChange={(e) => onChange({ ...form, location: e.target.value })}
            placeholder="e.g., Home safe, Storage unit"
          />
        </div>
      </div>

      {showUpload && (
        <UploadZone
          label="Attach any relevant documentation"
          labelSuffix="(optional)"
          file={form.file}
          onFileSelect={(file) => onChange({ ...form, file })}
          onFileRemove={() => {
            onChange({ ...form, file: null });
            onFileRemoved?.();
          }}
          existingFileName={existingFileName}
        />
      )}
    </div>
  );
};
